import type { MessageRating, RunUsage, ThreadMessage } from "@rakazo/contracts";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import type { SpaceQueries } from "../../lib/query";
import type { createSpaceRpc } from "../../lib/rpc";
import type { ReplyDetails } from "./reply-footer";
import { replyFooterIds } from "./reply-footer";

const NO_RATINGS = { ratings: [] as Array<{ messageId: string; rating: MessageRating }> };

/** The newest runs whose usage the server reads at once. */
const USAGE_RUN_LIMIT = 100;

/**
 * Ratings and run usage for the replies in a bot's chat. Usage shows under every finished reply,
 * whatever the tool activity preference. A rating shows at once and is undone if the server
 * refuses it.
 */
export function useReplyDetails({
  rpc,
  queries,
  botId,
  messages,
}: {
  rpc: ReturnType<typeof createSpaceRpc>;
  queries: SpaceQueries;
  botId: string | undefined;
  messages: readonly ThreadMessage[];
}): ReplyDetails | undefined {
  const queryClient = useQueryClient();
  const ratingsOptions = queries.replies.ratings.queryOptions({ input: { botId: botId ?? "" } });
  const ratingsQuery = useQuery({ ...ratingsOptions, enabled: Boolean(botId) });
  const runIds = useMemo(() => {
    const footers = replyFooterIds(messages);
    const ids = messages.flatMap((message) =>
      footers.has(message.id) && message.runId ? [message.runId] : [],
    );
    return [...new Set(ids)].slice(-USAGE_RUN_LIMIT);
  }, [messages]);
  const usageQuery = useQuery({
    ...queries.replies.usage.queryOptions({ input: { botId: botId ?? "", runIds } }),
    enabled: Boolean(botId) && runIds.length > 0,
    placeholderData: keepPreviousData,
  });
  const ratings = useMemo(
    () =>
      new Map((ratingsQuery.data ?? NO_RATINGS).ratings.map((row) => [row.messageId, row.rating])),
    [ratingsQuery.data],
  );
  const usage = useMemo(
    () => new Map<string, RunUsage>((usageQuery.data?.runs ?? []).map((run) => [run.runId, run])),
    [usageQuery.data],
  );
  const onRate = useCallback(
    (message: ThreadMessage, rating: MessageRating | null) => {
      if (!botId) return;
      const key = queries.replies.ratings.queryKey({ input: { botId } });
      const previous = queryClient.getQueryData(key);
      queryClient.setQueryData(key, (current) => {
        const rows = (current ?? NO_RATINGS).ratings.filter((row) => row.messageId !== message.id);
        return { ratings: rating ? [...rows, { messageId: message.id, rating }] : rows };
      });
      rpc.replies.rate({ botId, messageId: message.id, rating }).catch(() => {
        queryClient.setQueryData(key, previous);
      });
    },
    [botId, queries, queryClient, rpc],
  );
  return useMemo(
    () => (botId ? { ratings, usage, onRate } : undefined),
    [botId, ratings, usage, onRate],
  );
}
